import type {
  ExportDate,
  ExportFormat,
  ExportMode,
  ExportOption,
  ExportType,
} from '@/modules/exports'
import type { DriverActivityDayReportQueryReportData } from './types.graphql'
import type { RefObject } from 'react'
import type { UserNode } from '../users'
import type { FontAwesomeIcon } from '@ur-solutions/react-components'

/* Nodes */

export interface RangeNode {
  type: 'regular' | 'issue' | 'specificCondition' | 'infringement'

  info?: {
    label?: string
    registrationNumbers?: string[]
    time?: any
    date?: Date
  }

  height?: string
  /** Hour value - where to start range */
  time: number
  /** Text to display in node */
  text?: string
  /** Background of range between this and next node, if any */
  background?: string
  /** Text to display in range between this and next node, if any */
  rangeText?: string
  /** Subtext to display in range between this and next node, if any */
  rangeSubText?: string
  /** If the node is vehicle node */
  vehicleRange?: boolean
  /** Link target, only works for issue nodes */
  href?: string

  icon?: FontAwesomeIcon
}

export type TypeChart = 'day' | 'week' | 'month'

export type ReportDataItem = [string, string]

export interface GeneratePDFParams {
  date: Date
  type: TypeChart
  report: DriverActivityDayReportQueryReportData | null
  user: Pick<UserNode, 'id' | 'fullName'> | null
  fullWeek?: boolean
}

export interface UsePDFGeneratorParams {
  scheduleRef: RefObject<HTMLElement>
  setCurrentData: (date: Date) => void
  setIsLoading: (loading: boolean) => void
  setTypePrint: (type: TypeChart) => void
  setStartHour: (hour: number) => void
  onErrorAuto: () => void
}

export interface RangeNodeCollection {
  issueNodes: RangeNode[][]
  infringementNodes: RangeNode[][]
  specificConditionNodes: RangeNode[][]
  workNodes: [RangeNode, RangeNode][]
  availableNodes: [RangeNode, RangeNode][]
  restNodes: [RangeNode, RangeNode][]
  pauseNodes: [RangeNode, RangeNode][]
  driveNodes: [RangeNode, RangeNode][]
  countryNodes: RangeNode[][]
  vehicleNodes: [RangeNode, RangeNode][]
}

/* Forms */

export interface ExportActivitiesForm {
  exportType: ExportType
  exportOption: ExportOption
  exportFormat: ExportFormat
  weekly: boolean
  vehicle: string | null
  modeType: ExportMode
  date?: Date
  dateEnd?: Date
  dateType?: ExportDate
}

export interface ActivitiesForm {
  exportType: ExportType
  exportOption: ExportOption
  exportFormat: ExportFormat
  useDateRange: boolean
  month: Date
  dateStart: Date
  dateEnd: Date
  user: string | null
  users: string[]
  orderBy: UserOrderBy
  includeAbsences?: boolean
  weekly: boolean
}

/* Other */

export interface DayReport {
  [key: string]: Date | string | boolean | null | undefined

  sortingDate: Date
  regNr: string | null

  startTime: string | null
  endTime: string | null
  dayStartLocation: string | null
  dayEndLocation: string | null

  timeDriving: string | null
  timeOvertime: string | null
  timeAvailability: string | null
  timeRest: string | null
  timePause: string | null
  timeOtherWork: string | null
  dataStatus: boolean | null
  approved?: boolean | null
}

export interface DaySummation {
  work: number
  rest: number
  driving: number
}

export interface WeekSummation {
  [key: string]: string | null

  timeDriving: string | null
  timeOvertime: string | null
  timeAvailability: string | null
  timeRest: string | null
  timePause: string | null
  timePaidRest: string | null
  timeOtherWork: string | null
  diet: string | null
  salaryDay: string | null
}

export type VehicleColorDictionary = {
  [key: string]: string
}

export enum UserOrderBy {
  FAMILY_NAME = 'last_name',
  USER_TYPES = 'user_types',
  INTERNAL_NUMBER = 'internal_number',
}

export enum AddonType {
  FIXED = 'FIXED',
  OVERRIDE = 'OVERRIDE',
  TIME = 'TIME',
}
